import React, { useState, useContext, useEffect, useRef } from "react";
import {
  LoadingOverlay,
  Text,
  Button,
  Group as MantineGroup,
  Stack,
  Box,
  Alert,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import EnglishLoadingWithCompletion from "./english-loading-with-completion";
import DataCollectionIntro from "./data-collection-intro";
import FormField from "./form-field";
import { DataContext } from "../context/DataContext";
import useStatusCheck from "../hooks/useStatusCheck";
import useEdgeStatusCheck from "../hooks/useEdgeStatusCheck";
import useAdjustInputSize from "../hooks/useAdjustInputSize";

const AgematchForm = ({
  title,
  subtitle,
  icon,
  componentKey,
  endpoint,
  config,
  buttonText,
  reAttempts,
  onApiResponse,
}) => {
  const { data, text, dispatch, settings } = useContext(DataContext);
  const theme = useMantineTheme();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(reAttempts);
  const [statusResponse, setStatusResponse] = useState(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasReceivedFailedResponse, setHasReceivedFailedResponse] = useState(false);
  const [showIntro, setShowIntro] = useState(true);
  const [agematchStep, setAgematchStep] = useState(0); // 0: name, 1: address, 2: DOB
  const inputSize = useAdjustInputSize();
  const topBarRef = useRef(null);
  const [topBarHeight, setTopBarHeight] = useState(0);
  const bounceTransition = "cubic-bezier(0.68, -0.55, 0.27, 1.55)";
  const lastConfirmationRef = useRef(null);
  const confirmationsReceivedRef = useRef([]);
  const waitingForConfirmationChangeRef = useRef(false);
  const initialLoadRef = useRef(true);
  const [currentAttempt, setCurrentAttempt] = useState(0);
  const [hasShownFormForCurrentAttempt, setHasShownFormForCurrentAttempt] = useState(false);
  const [processingVerification, setProcessingVerification] = useState(false);
  const [loaderComplete, setLoaderComplete] = useState(false);

  const buttonContentColor =
    data.styles?.components?.button?.color ||
    (theme.colorScheme === "dark" ? theme.white : theme.black);

  // Field grouping for agematch steps
  const getAgematchFieldGroups = () => {
    const nameFields = config.fields.filter((field) => field.group === "name");
    const addressFields = config.fields.filter((field) => field.type === "address");

    console.log("AgeMatch field groups:", {
      nameFields,
      addressFields,
      allFields: config.fields,
      step: agematchStep
    });

    const dobFields = config.fields.filter(
      (field) =>
        field.type === "date" ||
        (field.group !== "name" && field.type !== "address"),
    );
    return { nameFields, addressFields, dobFields };
  };

  const { nameFields, addressFields, dobFields } = getAgematchFieldGroups();

  const getCurrentAgematchFields = () => {
    switch (agematchStep) {
      case 0: return nameFields;
      case 1: return addressFields;
      case 2: return dobFields;
      default: return nameFields;
    }
  };

  const getAgematchStepTitle = () => {
    switch (agematchStep) {
      case 0: return "Name details";
      case 1: return "Address details";
      case 2: return "Date of birth and other details";
      default: return "Name details";
    }
  };

  const isAgematchStepValid = () => {
    const currentFields = getCurrentAgematchFields();
    return currentFields.every((field) => {
      if (field.type === "address") {
        const addressValue = {
          addr: form.values.addr || "",
          city: form.values.city || "",
          state: form.values.state || "",
          zip: form.values.zip || "",
        };
        const validationResult = field.validation ? field.validation(addressValue) : null;
        return validationResult === null;
      } else {
        const value = form.values[field.name] || "";
        const validationResult = field.validation ? field.validation(value) : null;
        return validationResult === null;
      }
    });
  };

  // Status check handler (same as basic form)
  const handleStatusResult = (statusResult, confirmationChanged) => {
    if (initialLoadRef.current && statusResult) {
      initialLoadRef.current = false;
    }

    setStatusResponse(statusResult);
    const confirmationNumber = statusResult.confirmation || "unknown";

    if (statusResult.status === "PASS") {
      try {
        dispatch({ type: "SET_STATUS", payload: "PASS" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
      } catch (err) {
        // Silent fail
      }
      setLoading(true);
      setProcessingVerification(false);
      return true;
    }

    if (
      statusResult.status === "FAIL" &&
      !hasShownFormForCurrentAttempt &&
      initialLoadRef.current
    ) {
      initialLoadRef.current = false;
      setCurrentAttempt(1);
      setHasShownFormForCurrentAttempt(true);
      setAttemptsLeft(reAttempts);
      setLoading(false);
      setProcessingVerification(false);
      setResult(false);
      return false;
    }

    if (waitingForConfirmationChangeRef.current && confirmationChanged) {
      waitingForConfirmationChangeRef.current = false;
      lastConfirmationRef.current = confirmationNumber;
      confirmationsReceivedRef.current.push(confirmationNumber);
      setHasShownFormForCurrentAttempt(false);

      if (statusResult.status !== "PASS" && attemptsLeft > 0) {
        setLoading(false);
        setProcessingVerification(false);
        setHasReceivedFailedResponse(true);
        setResult(false);
        return false;
      }

      if (statusResult.status !== "PASS" && attemptsLeft === 0) {
        try {
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        } catch (err) {
          // Silent fail
        }
      }

      setLoading(true);
      return true;
    }

    return true;
  };

  useEffect(() => {
    if (topBarRef.current) {
      setTopBarHeight(topBarRef.current.offsetHeight);
    }
  }, [showIntro]);

  // Reset to first step on reattempt
  useEffect(() => {
    if (attemptsLeft < reAttempts && hasReceivedFailedResponse) {
      setAgematchStep(0);
      form.clearErrors();
      setHasReceivedFailedResponse(false);
    }
  }, [attemptsLeft, reAttempts, hasReceivedFailedResponse]);

  // Status check hooks
  useStatusCheck(result, false, componentKey, handleStatusResult);

  // Form setup
  const form = useForm({
    initialValues: config.fields.reduce((acc, field) => {
      if (field.type === "address") {
        acc["addr"] = data.addr || "";
        acc["city"] = data.city || "";
        acc["state"] = data.state || "";
        acc["zip"] = data.zip || "";
      } else {
        acc[field.name] = data[field.name] || field.initialValue || "";
      }
      return acc;
    }, {}),
    validateInputOnChange: true,
    validateInputOnBlur: true,
    validate: (values) => {
      const errors = {};
      config.fields.forEach((field) => {
        if (field.validation) {
          let error;
          if (field.name === "address" && field.type === "address") {
            error = field.validation({
              addr: values.addr || "",
              city: values.city || "",
              state: values.state || "",
              zip: values.zip || "",
            });
          } else {
            error = field.validation(values[field.name]);
          }
          if (error) {
            if (field.type === "address") {
              errors.addr = error;
            } else {
              errors[field.name] = error;
            }
          }
        }
      });
      return errors;
    },
  });

  // Submit handler
  const submitHandler = async () => {
    setIsVerifying(true);
    const errors = form.validate();

    if (errors.hasErrors) {
      setIsVerifying(false);
      return;
    }

    const newAttemptsLeft = attemptsLeft - 1;
    setAttemptsLeft(newAttemptsLeft);

    Object.keys(form.values).forEach((key) => {
      data[key] = form.values[key];
    });

    if (!data.settings) data.settings = {};
    data.settings.currentAttempt = currentAttempt;
    data.settings.attemptsLeft = newAttemptsLeft;
    data.attemptsLeft = newAttemptsLeft;

    setLoading(true);

    try {
      let apiUrl;
      let requestData = { ...data };

      if (data?.edge === true) {
        apiUrl = process.env.REACT_APP_EDGE_QUEUE_URL;
        requestData.queue = "idmatchplus";
      } else {
        apiUrl = `${process.env.REACT_APP_BACKEND_URL}${endpoint}`;
      }

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestData),
      })
        .then((res) => res.json())
        .then((responseData) => {
          if (typeof onApiResponse === "function") {
            onApiResponse(responseData);
          }

          try {
            dispatch({ type: "SET_STATUS", payload: "PENDING" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
          } catch (err) {
            // Silent fail
          }

          setTimeout(() => {
            setLoading(true);
            setProcessingVerification(true);
            setLoaderComplete(false);
            setIsVerifying(false);
          }, 2000);

          setCurrentAttempt((prevAttempt) => prevAttempt + 1);
          setHasShownFormForCurrentAttempt(false);
          waitingForConfirmationChangeRef.current = true;
          setResult(true);
        });
    } catch (error) {
      alert("Something Went Wrong! Please try again.");
      setIsVerifying(false);
    }
  };

  const handleDobChange = (dob) => {
    const formattedDob = `${dob.year}${dob.month}${dob.day}`;
    form.setFieldValue("dob", formattedDob);
  };

  const handleAddressChange = (address) => {
    form.setFieldValue("addr", address.addr);
    form.setFieldValue("city", address.city);
    form.setFieldValue("state", address.state);
    form.setFieldValue("zip", address.zip);
  };

  // Initial load effect
  useEffect(() => {
    setCurrentAttempt(0);
    setHasShownFormForCurrentAttempt(false);
    setAttemptsLeft(reAttempts);
    setLoading(false);
    form.clearErrors();
  }, [componentKey]);

  // Check if browser language is English for loading component
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "";
  const isEnglish = browserLanguage.toLowerCase().startsWith("en");

  if (loading && !isVerifying) {
    if (processingVerification && isEnglish) {
      const verificationMessages = [
        "Verifying age...",
        "Checking personal information...",
        "Validating address...",
        "Processing verification request...",
        "Finalizing age verification...",
      ];

      return (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            zIndex: 9999,
            backgroundColor:
              theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box sx={{ flex: 1, display: "flex" }}>
            <EnglishLoadingWithCompletion
              messages={verificationMessages}
              finalMessageDuration={4000}
              alwaysAnimateFinalStep={true}
              onComplete={() => {
                console.log("✅ Agematch verification loader animation complete");
                setLoaderComplete(true);
              }}
            />
          </Box>
        </Box>
      );
    }
    return <LoadingOverlay visible />;
  }

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes slideUpBounce {
            0% {
              opacity: 0;
              transform: translateY(20px);
            }
            60% {
              opacity: 1;
              transform: translateY(-5px);
            }
            80% {
              transform: translateY(3px);
            }
            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}
      </style>
      <form
        onSubmit={form.onSubmit(submitHandler)}
        style={{
          display: "flex",
          flexDirection: "column",
          animation: "fadeIn 0.6s ease-out",
          minHeight: "100%",
          maxWidth: "500px",
          margin: "0 auto",
          width: "100%",
        }}
      >
        {!showIntro && (
          <Box
            ref={topBarRef}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              zIndex: 5,
              width: "100%",
              backgroundColor:
                theme.colorScheme === "dark" ? theme.colors.dark[7] : theme.white,
              borderBottom: `1px solid ${
                theme.colorScheme === "dark"
                  ? theme.colors.dark[5]
                  : theme.colors.gray[3]
              }`,
              padding: "4px 0",
            }}
          >
            <Text
              fz="sm"
              fw={700}
              ta="center"
              c={theme.colorScheme === "dark" ? theme.colors.gray[2] : theme.black}
            >
              {getAgematchStepTitle()}
            </Text>
          </Box>
        )}

        <Box
          style={{
            marginTop: !showIntro ? topBarHeight : 0,
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showIntro && (
            <DataCollectionIntro
              icon={icon}
              title={title}
              componentKey={componentKey}
              inputSize={inputSize}
              data={data}
              onStart={() => setShowIntro(false)}
              bounceTransition={bounceTransition}
              theme={theme}
            />
          )}

          {!showIntro && (
            <Box
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <Stack spacing="md" style={{ flexGrow: 1, width: "100%" }}>
                {attemptsLeft < reAttempts && !isVerifying && (
                  <Alert
                    mt="xl"
                    mb="md"
                    title="Whoops! That did not go as expected"
                    styles={{
                      root: {
                        backgroundColor:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[9]
                            : theme.colors.red[0],
                        color:
                          theme.colorScheme === "dark"
                            ? theme.colors.red[0]
                            : theme.colors.red[9],
                      },
                      title: {
                        fontSize: "md",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                      message: {
                        fontSize: "sm",
                        color: theme.colorScheme === "dark" ? theme.white : theme.black,
                      },
                    }}
                  >
                    Please check that you've entered your full legal name, and provided an accurate address and date of birth.
                  </Alert>
                )}

                {/* Current step fields */}
                <div>
                  {/* Name fields (step 0) */}
                  {agematchStep === 0 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {nameFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* Address fields (step 1) */}
                  {agematchStep === 1 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {addressFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}

                  {/* DOB and other fields (step 2) */}
                  {agematchStep === 2 && (
                    <div
                      style={{
                        width: "100%",
                        animation: `slideUpBounce 0.5s ${bounceTransition}`
                      }}
                    >
                      <Stack spacing="md" style={{ width: "100%" }}>
                        {dobFields.map((field) => (
                          <FormField
                            key={field.name}
                            field={field}
                            form={form}
                            settings={settings}
                            componentKey={componentKey}
                            showMoreFields={true}
                            onDobChange={handleDobChange}
                            onAddressChange={handleAddressChange}
                          />
                        ))}
                      </Stack>
                    </div>
                  )}
                </div>

                {/* Back button for steps 1 and 2 */}
                {agematchStep > 0 && (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <Button
                      variant="subtle"
                      size="sm"
                      onClick={() => setAgematchStep(agematchStep - 1)}
                      c={theme.colorScheme === "dark" ? theme.colors.gray[4] : theme.colors.gray[6]}
                      sx={{
                        fontSize: "13px",
                        height: "32px",
                        padding: "0 16px",
                        "&:hover": {
                          backgroundColor:
                            theme.colorScheme === "dark"
                              ? theme.colors.dark[5]
                              : theme.colors.gray[1],
                        },
                      }}
                    >
                      ← Back
                    </Button>
                  </div>
                )}
              </Stack>

              {/* Button positioned at bottom */}
              <Box style={{ marginTop: "15px", marginBottom: "20px", width: "100%" }}>
                <Button
                  fullWidth
                  size="lg"
                  type={agematchStep === 2 ? "submit" : "button"}
                  disabled={isVerifying || !isAgematchStepValid()}
                  loading={false}
                  onClick={(e) => {
                    if (agematchStep < 2) {
                      e.preventDefault();
                      setAgematchStep(agematchStep + 1);
                    }
                  }}
                  sx={{
                    transition: `all 0.5s ${bounceTransition}`,
                    "&:hover": { transform: "scale(1.01)" },
                    '&[data-loading="true"]': { color: buttonContentColor },
                  }}
                >
                  {isVerifying ? (
                    <MantineGroup spacing="xs" noWrap>
                      <div
                        style={{
                          width: "16px",
                          height: "16px",
                          border: "2px solid transparent",
                          borderTop: `2px solid ${buttonContentColor}`,
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      <Text c="inherit">Sending Data</Text>
                    </MantineGroup>
                  ) : agematchStep === 2 ? (
                    buttonText
                  ) : (
                    "Continue"
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  );
};

export default AgematchForm;